<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { apiGet } from "../api";
import Pager from "../components/Pager.vue";
import SortTh from "../components/SortTh.vue";

interface Money { amount: number }
interface CustomerRow { id: string; name?: string; email: string; city?: string; orderCount: number; totalSpent: Money; lastOrderAt?: string }

const euro = (c: number) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(c / 100);
const datum = (s?: string) => (s ? new Date(s).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) : "—");
const initials = (n?: string, e?: string) => (n || e || "?").slice(0, 2).toUpperCase();

const router = useRouter();
const rows = ref<CustomerRow[]>([]);
const loading = ref(true);
const error = ref("");
const limit = 50;
const offset = ref(0);
const total = ref(0);
const sort = ref("created");
const order = ref<"asc" | "desc">("desc");

async function load() {
  loading.value = true;
  const qs = new URLSearchParams({ limit: String(limit), offset: String(offset.value), sort: sort.value, order: order.value });
  try {
    const d = await apiGet<{ items: CustomerRow[]; total: number }>(`/customers?${qs.toString()}`, { auth: true });
    rows.value = d.items; total.value = d.total;
  } catch (e) { error.value = String(e); } finally { loading.value = false; }
}
function reload() { if (offset.value !== 0) offset.value = 0; else load(); }
function setSort(col: string) {
  if (sort.value === col) order.value = order.value === "asc" ? "desc" : "asc";
  else { sort.value = col; order.value = "asc"; }
}
watch([sort, order], reload);
watch(offset, load);
onMounted(load);
</script>

<template>
  <div>
    <h1 class="title">Klanten <span class="count">· {{ total }}</span></h1>
    <p v-if="loading" class="muted">Laden…</p>
    <p v-else-if="error" class="err">{{ error }}</p>
    <table v-else class="tbl">
      <thead><tr>
        <SortTh label="Klant" col="name" :sort="sort" :order="order" @sort="setSort" />
        <th>Plaats</th><th>Bestellingen</th><th>Besteed</th><th>Laatste</th>
      </tr></thead>
      <tbody>
        <tr v-for="c in rows" :key="c.id" class="clickrow" @click="router.push(`/klanten/${c.id}`)">
          <td><div class="cust"><span class="avatar sm">{{ initials(c.name, c.email) }}</span><div><strong>{{ c.name || '—' }}</strong><div class="sub">{{ c.email }}</div></div></div></td>
          <td>{{ c.city || '—' }}</td>
          <td>{{ c.orderCount }}</td>
          <td>{{ euro(c.totalSpent.amount) }}</td>
          <td>{{ datum(c.lastOrderAt) }}</td>
        </tr>
      </tbody>
    </table>
    <Pager v-if="!loading && !error" v-model:offset="offset" :limit="limit" :total="total" />
  </div>
</template>

<style scoped>
.title { font-family: var(--font-ui); font-weight: 700; font-size: 24px; margin: 0 0 18px; }
.count { color: var(--muted); font-weight: 600; }
.muted { color: var(--muted); } .err { color: var(--red); }
.tbl { width: 100%; border-collapse: collapse; font-size: 14px; }
.tbl th { text-align: left; color: var(--muted); font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; padding: 8px 12px; border-bottom: 1px solid var(--hairline); }
.tbl td { padding: 12px; border-bottom: 1px solid var(--hairline); }
.clickrow { cursor: pointer; } .clickrow:hover { background: var(--surface-off); }
.cust { display: flex; align-items: center; gap: 10px; }
.sub { color: var(--muted); font-size: 12px; }
.avatar { width: 44px; height: 44px; border-radius: 999px; background: var(--coral-wash); color: var(--coral-wash-text); display: grid; place-items: center; font-weight: 700; }
.avatar.sm { width: 34px; height: 34px; font-size: 13px; }
</style>
